/**
 * Custom Founders Story - Carousel functionality
 * Handles scroll-snap carousel with arrow navigation and dot pagination
 */
(() => {
  'use strict';

  const ROOT_SELECTOR = '[data-founders-story]';
  const CAROUSEL_SELECTOR = '[data-founders-carousel]';
  const SLIDE_SELECTOR = '.c-founders-story__slide';
  const DOT_SELECTOR = '[data-founders-dot]';
  const PREV_SELECTOR = '[data-founders-prev]';
  const NEXT_SELECTOR = '[data-founders-next]';

  class FoundersStoryCarousel {
    constructor(root) {
      this.root = root;
      this.carousel = root.querySelector(CAROUSEL_SELECTOR);
      this.slides = Array.from(root.querySelectorAll(SLIDE_SELECTOR));
      this.dots = Array.from(root.querySelectorAll(DOT_SELECTOR));
      this.prevButton = root.querySelector(PREV_SELECTOR);
      this.nextButton = root.querySelector(NEXT_SELECTOR);
      this.captionElement = root.querySelector('[data-founders-caption]');
      this.headingElement = root.querySelector('[data-founders-heading]');
      this.bodyElement = root.querySelector('[data-founders-body]');

      if (!this.carousel || this.slides.length === 0) {
        return;
      }

      this.currentIndex = 0;
      this.isScrolling = false;

      this.init();
    }

    init() {
      this.setupListeners();
      this.updateState();
    }

    setupListeners() {
      // Arrow navigation
      if (this.prevButton) {
        this.prevButton.addEventListener('click', () => this.goToPrev());
      }

      if (this.nextButton) {
        this.nextButton.addEventListener('click', () => this.goToNext());
      }

      // Dot navigation
      this.dots.forEach((dot) => {
        dot.addEventListener('click', () => {
          const index = parseInt(dot.dataset.index, 10) || 0;
          this.goToSlide(index);
        });
      });

      // Scroll detection for snap
      this.carousel.addEventListener('scroll', this.handleScroll.bind(this), { passive: true });

      // Keyboard navigation
      this.root.addEventListener('keydown', this.handleKeydown.bind(this));
    }

    handleScroll() {
      if (this.isScrolling) return;

      // Debounce scroll detection
      clearTimeout(this.scrollTimeout);
      this.scrollTimeout = setTimeout(() => {
        const scrollLeft = this.carousel.scrollLeft;
        const slideWidth = this.slides[0]?.offsetWidth || 0;

        if (slideWidth > 0) {
          const newIndex = Math.round(scrollLeft / slideWidth);
          if (newIndex !== this.currentIndex && newIndex >= 0 && newIndex < this.slides.length) {
            this.currentIndex = newIndex;
            this.updateState();
          }
        }
      }, 50);
    }

    handleKeydown(event) {
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        this.goToPrev();
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        this.goToNext();
      }
    }

    goToPrev() {
      if (this.currentIndex > 0) {
        this.goToSlide(this.currentIndex - 1);
      }
    }

    goToNext() {
      if (this.currentIndex < this.slides.length - 1) {
        this.goToSlide(this.currentIndex + 1);
      }
    }

    goToSlide(index) {
      if (index < 0 || index >= this.slides.length || index === this.currentIndex) {
        return;
      }

      this.isScrolling = true;
      this.currentIndex = index;

      const slide = this.slides[index];
      if (slide && this.carousel) {
        // Scroll within the carousel container only, prevent page scroll
        const slideLeft = slide.offsetLeft;
        this.carousel.scrollTo({
          left: slideLeft,
          behavior: 'smooth'
        });
      }

      this.updateState();

      // Reset scrolling flag after animation
      setTimeout(() => {
        this.isScrolling = false;
      }, 500);
    }

    updateState() {
      // Update dots
      this.dots.forEach((dot, index) => {
        dot.classList.toggle('is-active', index === this.currentIndex);
      });

      // Update arrows
      if (this.prevButton) {
        this.prevButton.disabled = this.currentIndex === 0;
      }

      if (this.nextButton) {
        this.nextButton.disabled = this.currentIndex === this.slides.length - 1;
      }

      const currentSlide = this.slides[this.currentIndex];
      if (!currentSlide) return;

      const slideData = currentSlide.querySelector('.c-founders-story__slide-data');
      const bodySpan = slideData?.querySelector('[data-slide-body]');
      const captionSpan = slideData?.querySelector('[data-slide-caption]');

      // Update heading
      if (this.headingElement) {
        const heading = currentSlide.dataset.heading || '';
        if (heading) {
          this.headingElement.textContent = heading;
          this.headingElement.style.display = '';
        } else {
          this.headingElement.textContent = '';
          this.headingElement.style.display = 'none';
        }
      }

      // Update body
      if (this.bodyElement) {
        const bodyHtml = (bodySpan?.innerHTML ?? '').trim();
        if (bodyHtml) {
          this.bodyElement.innerHTML = bodyHtml;
          this.bodyElement.style.display = '';
        } else {
          this.bodyElement.innerHTML = '';
          this.bodyElement.style.display = 'none';
        }
      }

      // Update caption
      if (this.captionElement) {
        const captionText = captionSpan?.textContent?.trim() || '';
        if (captionText) {
          this.captionElement.textContent = captionText;
          this.captionElement.style.display = 'block';
        } else {
          this.captionElement.textContent = '';
          this.captionElement.style.display = 'none';
        }
      }
    }
  }

  // Initialize all instances
  const initAll = () => {
    document.querySelectorAll(ROOT_SELECTOR).forEach((root) => {
      if (root.dataset.foundersStoryInitialized === 'true') {
        return;
      }

      root.dataset.foundersStoryInitialized = 'true';
      new FoundersStoryCarousel(root);
    });
  };

  // Initialize on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll, { once: true });
  } else {
    initAll();
  }

  // Re-initialize on section load (for theme editor)
  document.addEventListener('shopify:section:load', (event) => {
    const root = event.target.querySelector(ROOT_SELECTOR);
    if (root) {
      delete root.dataset.foundersStoryInitialized;
      new FoundersStoryCarousel(root);
    }
  });
})();
